/**
 * What Pages needs from the project's tracker room, provided once around the
 * whole Pages shell: the `TrackersUIProvider` the typed page, the type page's
 * table and the tree's type resolver read, the resolver itself, the editor
 * host hooks (mark author, citations) and the placed-view host.
 *
 * The provider is always mounted, with no data source until the room opens,
 * so the tree and an open editor below it never remount when it does.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TrackersUIProvider, type TeamMemberOption } from '@nimbalyst/collab-bundle/trackers-ui';
import type { CollabTypeTreeResolver } from '@nimbalyst/collab-bundle/docs-ui';
import { orgDocumentPath, orgTypePagePath, orgPageItemPath, type RouteTeam } from '../routing';
import { useOptionalProjectTrackerSession } from '../trackers/ProjectTrackerSession';
import type { BrowserTrackerSession } from '../trackers/useBrowserTrackerSession';
import { consolePathForHref, pageUriPath } from './pagesLinks';
import { usePlacedViewHost, type PlacedViewRoom } from './PlacedViewHost';
import { installWebEditorHostHooks } from './webEditorHostHooks';
import { usePageModule, type PageModule } from './pageModule';
import { pagesPlacedViewTypes } from './placedViewTypes';
import { notifyWebTypedPageMarksChanged } from '../collab/webPageMarks';
import { memberDisplayName } from '../collab/orgRoster';

export interface PagesTrackersValue {
  /** Names placed types and lists their items; absent until the tracker room opens. */
  resolver?: CollabTypeTreeResolver;
  /** True once the project's tracker room is open in this tab. */
  available: boolean;
  /** Archive a typed page through the tracker room; absent until the room opens. */
  archiveItem?: (itemId: string) => Promise<void>;
  /** Create a new team type (Pages "New type..."); absent until the room opens. */
  defineType?: BrowserTrackerSession['defineType'];
}

const PagesTrackersContext = createContext<PagesTrackersValue>({ available: false });

export function usePagesTrackers(): PagesTrackersValue {
  return useContext(PagesTrackersContext);
}

function TypeResolverBridge({ module, onResolver }: { module: PageModule; onResolver: (resolver: CollabTypeTreeResolver) => void }) {
  const resolver = module.useBrowserTypeResolver('team');
  useEffect(() => { onResolver(resolver); }, [onResolver, resolver]);
  return null;
}

export function PagesTrackersProvider({
  team,
  projectId,
  memberId,
  navigate,
  children,
}: {
  team: RouteTeam;
  /** The team project id; console routes use it as the project segment. */
  projectId: string;
  memberId: string;
  navigate: (path: string) => void;
  children: ReactNode;
}) {
  const project = useOptionalProjectTrackerSession();
  const dataSource = project?.session?.dataSource ?? null;
  useEffect(() => dataSource?.subscribe(notifyWebTypedPageMarksChanged), [dataSource]);
  const defineType = project?.session?.defineType;
  const identity = project?.identity ?? null;
  // Who a table's `user` cells can name.
  const teamMembers = useMemo<TeamMemberOption[]>(
    () => (project?.members ?? []).map((member) => ({ email: member.email, name: memberDisplayName(member) })),
    [project?.members],
  );
  const trackerTypes = project?.session?.schema.trackerTypes;
  // The editors' placed-view slash entries list this project's types.
  useEffect(() => { pagesPlacedViewTypes.set(trackerTypes ?? []); }, [trackerTypes]);
  const [resolver, setResolver] = useState<CollabTypeTreeResolver>();
  const pageModule = usePageModule();

  // Read late through refs: the roster can answer after the hooks are installed,
  // and `navigate` changes identity on every location change.
  const latest = useRef({ project, navigate, team, projectId });
  latest.current = { project, navigate, team, projectId };
  useEffect(() => installWebEditorHostHooks({
    getAuthor: () => {
      const self = latest.current.project?.members.find((member) => member.memberId === memberId);
      return self ? { name: self.name || null, email: self.email || null } : null;
    },
    navigate: (path) => latest.current.navigate(path),
    pagePath: (documentId) => orgDocumentPath(latest.current.team, latest.current.projectId, documentId),
  }), [memberId]);

  const room = useMemo<PlacedViewRoom | null>(() => (dataSource ? {
    dataSource,
    identity,
    teamMembers,
    orgId: team.orgId,
    projectId,
    onOpenFullView: (typeId, view) => latest.current.navigate(`${orgTypePagePath(latest.current.team, latest.current.projectId, typeId)}?${new URLSearchParams({ view: JSON.stringify(view) })}`),
    onOpenItem: (itemId) => latest.current.navigate(orgPageItemPath(latest.current.team, latest.current.projectId, itemId)),
    onOpenPage: (uri) => {
      const path = pageUriPath(uri, latest.current.team, latest.current.projectId);
      if (path) latest.current.navigate(path);
    },
    onOpenLink: (href) => {
      const path = consolePathForHref(href, window.location.origin);
      if (path) latest.current.navigate(path);
      else window.location.href = href;
    },
  } : null), [dataSource, identity, teamMembers, team.orgId, projectId]);
  usePlacedViewHost(room);

  const value = useMemo<PagesTrackersValue>(
    () => ({
      resolver: dataSource ? resolver : undefined,
      available: Boolean(dataSource),
      archiveItem: dataSource
        ? async (itemId: string) => { await dataSource.command({ type: 'archive-item', itemId, archive: true }); }
        : undefined,
      defineType: dataSource ? defineType : undefined,
    }),
    [dataSource, resolver, defineType],
  );
  return (
    <TrackersUIProvider dataSource={dataSource} identity={identity} teamMembers={teamMembers}>
      {dataSource && pageModule ? <TypeResolverBridge module={pageModule} onResolver={setResolver} /> : null}
      <PagesTrackersContext.Provider value={value}>{children}</PagesTrackersContext.Provider>
    </TrackersUIProvider>
  );
}
